import { MouseEvent } from "react";
import { Box, Button, Text, Flex, Heading, Badge, BadgeProps, Checkbox } from "@/ui";
import Time from "@/lib/Time";
import { isStationOffline } from "@/utils/isStationOffline";
import { t } from "@/utils";
import { EventColors, EventIcons, EventStringKeys } from "@/enums/events";
import { CameraIcons, CameraColors } from "@/enums/camera";
import useToast from "@/hooks/useToast";
import { getCameraDevices, getDeviceByObjectId } from "@/store/devices/selectors";
import { useDevices } from "@/store/devices";
import { usePresenceKeeper } from "@/modules/app/Presence";
import { useEventDeletion } from "viewer/store/eventDeletion";
import { getIsMarkedForDeletion } from "viewer/store/eventDeletion/selectors";
import { useEventFilters } from "viewer/store/eventFilters";
import { getActiveConfig } from "viewer/store/eventFilters/selectors";
import { useViewer } from "viewer/store/viewer";
import Circle from "@/components/Circle";
import Icon from "@/components/Icon";
import EventThumbnail from "./EventThumbnail";

import WarningIcon from "assets/icons/warning.svg?react";

type Props = {
  isSelected: boolean;
  onSelect: Cb;
  event: ParseCameraEvent;
};
const commonBadgeStyles: BadgeProps = {
  px: "6",
  py: "2",
  fontSize: "9px",
  pos: "absolute",
  top: "6",
  right: "6",
  variant: "status",
  zIndex: 2
};

export default function EventCard({ isSelected, onSelect, event }: Props) {
  const { type, end, start, cameraStation } = event;

  const { device, cameraDevices } = useDevices((s) => ({
    device: getDeviceByObjectId(cameraStation.objectId)(s) as StoreCameraDevice,
    cameraDevices: getCameraDevices(s)
  }));
  const presence = usePresenceKeeper()[device?.jid] || "unavailable";
  const cameraPreview = useViewer((s) => s.cameraPreviews)[device?.jid] || null;

  const filterConfig = useEventFilters(getActiveConfig);
  const isMarkedForDeletion = useEventDeletion(getIsMarkedForDeletion(event.objectId));
  const [isInDeletionMode, addOrRemove, addMultiple] = useEventDeletion((s) => [
    s.isInDeletionMode,
    s.addOrRemove,
    s.addMultiple
  ]);
  const toast = useToast();

  const displayCameraIcon = cameraDevices.length > 1 && filterConfig.key === "fromDashboard";
  const isOngoingAndLive = Boolean(!event.end) && !isStationOffline(presence);
  const isOngoingAndOffline = Boolean(!event.end) && isStationOffline(presence);
  const canBeDeleted = event.end && event.intervalEvent;

  const thumbnailUrl = isOngoingAndLive ? cameraPreview?.image?.url : event.thumbnailUrl;

  const handleClick = (e: MouseEvent) => {
    if (isInDeletionMode) {
      if (!canBeDeleted) {
        toast.show({
          title: t(isOngoingAndLive ? "ongoingEventCantBeSelected" : "unfinishedEventCantBeSelected"),
          icon: WarningIcon,
          iconFill: "error"
        });
        return;
      }
      if (e.shiftKey) addMultiple(event.objectId);
      else addOrRemove(event.objectId);
    } else onSelect();
  };

  return (
    <Button
      onClick={handleClick}
      variant="custom"
      borderRadius="1rem"
      transition="background-color 0.2s"
      bg="surface3"
      w="100%"
      h={["80px", null, "90px"]}
      alignItems="center"
      overflow="hidden"
      pos="relative"
    >
      {isOngoingAndLive && <Badge {...commonBadgeStyles}>{t("live").toUpperCase()}</Badge>}
      {isOngoingAndOffline && (
        <Badge bg="secondary" color="on-secondary" {...commonBadgeStyles}>
          {t("notAvailable").toUpperCase()}
        </Badge>
      )}
      <Box
        w="4px"
        pos="absolute"
        left="0px"
        bg="primary"
        height="100%"
        transition="opacity 0.2s"
        opacity={isSelected ? 1 : 0}
      />
      {isInDeletionMode && (
        <Checkbox isDisabled={!canBeDeleted} ml="0.75rem" isChecked={isMarkedForDeletion} pointerEvents="none" />
      )}

      <Circle size="2rem" bg={EventColors[event.type].secondary} props={{ mr: "0.75rem", ml: "0.75rem" }}>
        <Icon icon={EventIcons[event.type]} fill={EventColors[event.type].primary} size="16" />
      </Circle>

      <Box flexGrow="1" textAlign="left" mr="12px" overflow="hidden">
        <Flex alignItems="center" mb="0.25rem">
          <Heading as="h6" variant="title" color="on-surface" mr="8px">
            {Time.formatDate(start, "HH:mm")}
          </Heading>
          {displayCameraIcon && device && (
            <Icon icon={CameraIcons[device.icon].icon} fill={CameraColors[device.color].value} size="1rem" />
          )}
          {!device && "Unknown camera"}
        </Flex>

        <Text color="on-surface-variant" variant="body">
          {t(EventStringKeys[type])} ·{" "}
          {end ? Time.formatDuration(end - start) : isOngoingAndLive ? t("ongoingEvent") : t("unfinishedEvent")}
        </Text>
      </Box>
      <EventThumbnail url={thumbnailUrl} hideBg={Boolean(end)} />
    </Button>
  );
}
